import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { BaseHttpClientService } from './base-http-client.service';
import { getFileNameFromPath } from 'app/_utils/file-path.utils';

@Injectable()
export class ContractorService extends BaseHttpClientService {

  constructor(private http: HttpClient) {
    super()
  }

  public getAllContractors(pagination, sort, filter) {
    const params = this.createQueryStrings(pagination, sort, filter);
    const headers = this.applyLatestOnlyHeader();

    return this.http.get(environment.apiUrl + 'api/ext-maintenance', { params, observe: 'response', headers })
      .pipe(map((response) => this.handlePaginatedData(response)))
      .pipe(catchError(this.handleError));
  }

  public getLinkedContractors(siteOwnerSkylarkId, pagination, sort, filter) {
    let paginationString = '';
    paginationString += '?size=' + pagination.size;
    paginationString += '&page=' + pagination.page;

    const sortString = '&sort=' + sort;

    let filterString = '';
    if (filter) {
      filterString = '&filter=' + filter;
    }

    const headers = this.applyLatestOnlyHeader();

    return this.http.get(environment.apiUrl + 'api/ext-maintenance/by-site-owner/' + siteOwnerSkylarkId + paginationString + sortString + filterString, { observe: 'response', headers })
      .pipe(map((response) => this.handlePaginatedData(response)))
      .pipe(catchError(this.handleError));
  }

  public getAllContractorsNoPagination() {
    return this.http.get(environment.apiUrl + 'api/ext-maintenance?size=10000&page=0&sort=name,asc')
      .pipe(catchError(this.handleError));
  }

  public getAllContractorFullList() {
    return this.http.get(environment.apiUrl + 'api/ext-maintenance/full-list')
      .pipe(catchError(this.handleError));
  }

  public getAllContractorSiteOwnerLinks(pagination, sort, filter) {
    const params = this.createQueryStrings(pagination, sort, filter);
    const headers = this.applyLatestOnlyHeader();

    return this.http.get(environment.apiUrl + 'api/ext-maintenance/maintenance-siteowners', { params, observe: 'response', headers })
      .pipe(map((response) => this.handlePaginatedData(response)))
      .pipe(catchError(this.handleError));
  }

  public createContractorSiteOwnerLink(body) {
    return this.http.post(environment.apiUrl + 'api/ext-maintenance/maintenance-siteowner', body)
      .pipe(catchError(this.handleError));
  }

  public getContractorById(id) {
    return this.http.get(environment.apiUrl + 'api/ext-maintenance/' + id)
      .pipe(catchError(this.handleError));
  }

  public createContractor(obj) {
    return this.http.post(environment.apiUrl + 'api/ext-maintenance/create', obj)
      .pipe(catchError(this.handleError))
  }

  public inviteContractor(obj) {
    return this.http.post(environment.apiUrl + 'api/ext-skylark-user/contractor/invite', obj)
      .pipe(catchError(this.handleError))
  }

  public addNewRequirementToContractor(obj) {
    return this.http.post(environment.apiUrl + 'api/ext-maintenance/maintenance-siteowner-competency', obj)
      .pipe(catchError(this.handleError))
  }

  public editContractor(obj) {
    return this.http.put(environment.apiUrl + 'api/ext-maintenance', obj)
      .pipe(catchError(this.handleError));
  }

  public setContractorManager(obj) {
    return this.http.post(environment.apiUrl + 'api/ext-maintenance/set-maintenance-manager', obj)
      .pipe(catchError(this.handleError));
  }

  public getContractorForCurrentUser(): any {
    return this.http.get(environment.apiUrl + 'api/ext-maintenance/current')
      .pipe(catchError(this.handleError));
  }

  public getAllContactsByContractorId(id, pagination) {
    let paginationString = '';
    paginationString += '?size=' + pagination.size;
    paginationString += '&page=' + pagination.page;
    return this.http.get(environment.apiUrl + 'api/ext-maintenance-contact/by-maintenance/' + id + paginationString, { observe: 'response' })
      .pipe(map((response) => this.handlePaginatedData(response)))
      .pipe(catchError(this.handleError));
  }

  public createContact(obj) {
    return this.http.post(environment.apiUrl + 'api/ext-maintenance-contact/create', obj)
      .pipe(catchError(this.handleError));
  }

  public editContact(obj) {
    return this.http.put(environment.apiUrl + 'api/ext-maintenance-contact', obj)
      .pipe(catchError(this.handleError));
  }

  public deleteContact(id) {
    return this.http.delete(environment.apiUrl + 'api/ext-maintenance-contact/' + id)
      .pipe(catchError(this.handleError));
  }

  public getAllWorkersForContractorNoPagination(id, sort: boolean) {
    const pagination = { size: 10000, page: 0, totalRecords: 0, }
    const sortStr = sort ? ['user.firstName,asc', 'user.lastName,asc'] : undefined;
    const params = this.createQueryStrings(pagination, sortStr);

    return this.http.get(environment.apiUrl + 'api/ext-skylark-user/worker-user/by-maintenance/' + id, { params })
      .pipe(catchError(this.handleError));
  }

  public getWorkerListForContractor(id, nameSort: boolean) {
    let params = new HttpParams().set('nameSort', nameSort.toString());
    return this.http.get(environment.apiUrl + 'api/ext-worker-user/by-maintenance/' + id, { params })
      .pipe(catchError(this.handleError));
  }

  public getAllWorkersForContractor(id, pagination, sort) {
    let paginationString = '';
    paginationString += '?size=' + pagination.size;
    paginationString += '&page=' + pagination.page;

    const sortString = '&sort=' + sort;

    return this.http.get(environment.apiUrl + 'api/ext-skylark-user/worker-user/by-maintenance/' + id + paginationString + sortString, { observe: 'response' })
      .pipe(map((response) => this.handlePaginatedData(response)))
      .pipe(catchError(this.handleError));
  }

  public getWorkerById(id) {
    return this.http.get(`${environment.apiUrl}api/ext-skylark-user/${id}`)
      .pipe(catchError(this.handleError));
  }

  public getSitesByWorkerId(id) {
    return this.http.get(environment.apiUrl + `api/ext-worker-user-site/by-skylark-user/${id}?size=999&page=0`)
      .pipe(catchError(this.handleError));
  }

  public createWorker(obj) {
    return this.http.post(environment.apiUrl + 'api/ext-skylark-user/worker-user-create', obj)
      .pipe(catchError(this.handleError));
  }

  public editWorker(obj) {
    return this.http.put(environment.apiUrl + 'api/ext-skylark-user/worker-user-update', obj)
      .pipe(catchError(this.handleError));
  }

  public updateWorkerPin(obj) {
    return this.http.put(environment.apiUrl + 'api/ext-skylark-user/update-password', obj)
      .pipe(catchError(this.handleError));
  }

  public getAllCompetenciesByCompany(id, pagination) {
    let paginationString = '';
    paginationString += '?size=' + pagination.size;
    paginationString += '&page=' + pagination.page;
    return this.http.get(environment.apiUrl + 'api/ext-maintenance/' + id + '/competencies' + paginationString, { observe: 'response' })
      .pipe(map((response) => this.handlePaginatedData(response)))
      .pipe(catchError(this.handleError));
  }

  public getSkylarkCompetencyImage(): Promise<Blob> {
    return this.http.get('/assets/img/placeholder-files/skylark-control-competency-added.jpg', { responseType: 'blob' }).pipe(map(blob => {
      return new File([blob], 'skylark-control-competency-added.jpg');
    })).toPromise();
  }

  public deleteContractorCertificateById(id) {
    return this.http.delete(environment.apiUrl + 'api/ext-maintenance/maintenance-siteowner-competency/' + id)
      .pipe(catchError(this.handleError));
  }

  public createContractorCompetency(maintenanceSiteOwnerCompetencyId, body, filesArray) {
    const jsonDataMultipartName = 'certFileUpload';
    const fileMultipartName = 'certFiles';

    // multipart/form-data, append each part
    const formData = new FormData();
    formData.append(jsonDataMultipartName, new Blob([JSON.stringify(body)], { type: 'application/json' }));

    filesArray.forEach(file => {
      formData.append(fileMultipartName, file, file.name)
    });

    return this.http.post(environment.apiUrl + `api/ext-maintenance/maintenance-siteowner-competency/${maintenanceSiteOwnerCompetencyId}/certificate_file`, formData)
      .pipe(catchError((error) => {
        console.log('Error', error.error);
        if (error.error && error.error.status === 400 && error.error.errorKey === 'Infected') {
          throw new Error('Infected');
        } else if (error.error && error.error.status === 400 && error.error.errorKey === 'EmbeddedScript') {
          throw new Error('EmbeddedScript');
        }
        return this.handleError(error);
      }));
  }

  public showFile(folder, fileName): void {
    const uri = folder + '/' + getFileNameFromPath(fileName);
    this.http.get(uri, { responseType: 'blob' })
      .subscribe(response => {
        const fileURL = URL.createObjectURL(response);
        if (response.type.startsWith('image')
          || response.type.endsWith('pdf')) {
          window.open(fileURL, '_blank');
        } else {
          const a = document.createElement('a');
          document.body.appendChild(a);
          a.setAttribute('style', 'display: none');
          a.href = fileURL;
          a.download = fileName;
          a.click();
          window.URL.revokeObjectURL(fileURL);
        }
      });
  }
}
